import { useState, useRef, useEffect, useCallback } from 'react';
import { subDays, startOfQuarter, format, parseISO, isAfter, isValid } from 'date-fns';
import { Calendar, ChevronDown, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AnalyticsFilters } from '@/types';

/** Date preset options for the filter bar */
export type DatePreset = 'last7' | 'last30' | 'last90' | 'qtd' | 'custom';

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  last7: 'Last 7 Days',
  last30: 'Last 30 Days',
  last90: 'Last 90 Days',
  qtd: 'Quarter-to-Date',
  custom: 'Custom Range',
};

const REGION_OPTIONS = ['West', 'Central', 'East'] as const;
export type Region = (typeof REGION_OPTIONS)[number];

export interface GlobalFilterBarProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
  /** Currently selected date preset */
  datePreset: DatePreset;
  onDatePresetChange: (preset: DatePreset) => void;
  /** Selected regions — empty array means "All" */
  selectedRegions: Region[];
  onRegionsChange: (regions: Region[]) => void;
}

/**
 * Compute start/end dates for a given preset.
 */
export function getPresetDates(preset: Exclude<DatePreset, 'custom'>): {
  startDate: string;
  endDate: string;
} {
  const today = new Date();
  const endDate = format(today, 'yyyy-MM-dd');

  switch (preset) {
    case 'last7':
      return { startDate: format(subDays(today, 6), 'yyyy-MM-dd'), endDate };
    case 'last30':
      return { startDate: format(subDays(today, 29), 'yyyy-MM-dd'), endDate };
    case 'last90':
      return { startDate: format(subDays(today, 89), 'yyyy-MM-dd'), endDate };
    case 'qtd':
      return { startDate: format(startOfQuarter(today), 'yyyy-MM-dd'), endDate };
  }
}

/**
 * Validate that startDate is not after endDate.
 */
export function validateDateRange(startDate: string, endDate: string): string | null {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (!isValid(start) || !isValid(end)) {
    return 'Invalid date format';
  }
  if (isAfter(start, end)) {
    return 'Start date cannot be after end date';
  }
  return null;
}

/**
 * Global filter bar for the analytics dashboard.
 * Provides date range presets, custom date range, and region multi-select.
 */
export function GlobalFilterBar({
  filters,
  onFiltersChange,
  datePreset,
  onDatePresetChange,
  selectedRegions,
  onRegionsChange,
}: GlobalFilterBarProps) {
  const [dateError, setDateError] = useState<string | null>(null);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef<HTMLDivElement>(null);

  // Close region dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(event.target as Node)
      ) {
        setRegionDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = useCallback(
    (preset: DatePreset) => {
      onDatePresetChange(preset);
      setDateError(null);

      if (preset !== 'custom') {
        const { startDate, endDate } = getPresetDates(preset);
        onFiltersChange({
          ...filters,
          startDate,
          endDate,
        });
      }
    },
    [filters, onFiltersChange, onDatePresetChange]
  );

  const handleCustomDateChange = useCallback(
    (field: 'startDate' | 'endDate', value: string) => {
      const newFilters = { ...filters, [field]: value };
      const error = validateDateRange(newFilters.startDate, newFilters.endDate);
      setDateError(error);

      if (!error) {
        onFiltersChange(newFilters);
      }
    },
    [filters, onFiltersChange]
  );

  const handleRegionToggle = useCallback(
    (region: Region) => {
      const newRegions = selectedRegions.includes(region)
        ? selectedRegions.filter((r) => r !== region)
        : [...selectedRegions, region];
      onRegionsChange(newRegions);

      onFiltersChange({
        ...filters,
        region: newRegions.length > 0 ? newRegions : undefined,
      });
    },
    [selectedRegions, onRegionsChange, filters, onFiltersChange]
  );

  const handleClearRegions = useCallback(() => {
    onRegionsChange([]);
    onFiltersChange({ ...filters, region: undefined });
  }, [onRegionsChange, filters, onFiltersChange]);

  const regionLabel =
    selectedRegions.length === 0
      ? 'All Regions'
      : selectedRegions.length === REGION_OPTIONS.length
        ? 'All Regions'
        : selectedRegions.join(', ');

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Date preset pills */}
        <div className="flex items-center gap-1">
          <Calendar className="h-4 w-4 text-[#555555] mr-1" aria-hidden="true" />
          {(Object.keys(DATE_PRESET_LABELS) as DatePreset[]).map((preset) => (
            <button
              key={preset}
              onClick={() => handlePresetClick(preset)}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded border transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB]',
                datePreset === preset
                  ? 'bg-[#6B4FBB] border-[#6B4FBB] text-white'
                  : 'bg-white border-neutral-200 text-[#111111] hover:bg-neutral-100'
              )}
              aria-pressed={datePreset === preset}
            >
              {DATE_PRESET_LABELS[preset]}
            </button>
          ))}
        </div>

        {/* Region multi-select dropdown */}
        <div className="relative ml-auto" ref={regionDropdownRef}>
          <button
            onClick={() => setRegionDropdownOpen(!regionDropdownOpen)}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB]',
              selectedRegions.length > 0 && selectedRegions.length < REGION_OPTIONS.length
                ? 'bg-[#6B4FBB] border-[#6B4FBB] text-white'
                : 'bg-white border-neutral-200 text-[#111111] hover:bg-neutral-100'
            )}
            aria-expanded={regionDropdownOpen}
            aria-haspopup="listbox"
          >
            {regionLabel}
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          </button>

          {regionDropdownOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-48 bg-white border border-neutral-200 rounded-lg shadow-sm z-10"
              role="listbox"
              aria-multiselectable="true"
              aria-label="Region filter"
            >
              {/* All option */}
              <button
                onClick={handleClearRegions}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-neutral-100 transition-colors',
                  selectedRegions.length === 0 && 'font-medium text-[#6B4FBB]'
                )}
                role="option"
                aria-selected={selectedRegions.length === 0}
              >
                <span
                  className={cn(
                    'flex items-center justify-center h-4 w-4 rounded border',
                    selectedRegions.length === 0
                      ? 'bg-[#6B4FBB] border-[#6B4FBB]'
                      : 'border-neutral-200'
                  )}
                >
                  {selectedRegions.length === 0 && (
                    <Check className="h-3 w-3 text-white" aria-hidden="true" />
                  )}
                </span>
                All
              </button>

              {REGION_OPTIONS.map((region) => (
                <button
                  key={region}
                  onClick={() => handleRegionToggle(region)}
                  className={cn(
                    'flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-neutral-100 transition-colors',
                    selectedRegions.includes(region) && 'font-medium text-[#6B4FBB]'
                  )}
                  role="option"
                  aria-selected={selectedRegions.includes(region)}
                >
                  <span
                    className={cn(
                      'flex items-center justify-center h-4 w-4 rounded border',
                      selectedRegions.includes(region)
                        ? 'bg-[#6B4FBB] border-[#6B4FBB]'
                        : 'border-neutral-200'
                    )}
                  >
                    {selectedRegions.includes(region) && (
                      <Check className="h-3 w-3 text-white" aria-hidden="true" />
                    )}
                  </span>
                  {region}
                </button>
              ))}

              {selectedRegions.length > 0 && (
                <div className="border-t border-neutral-200 px-3 py-2">
                  <button
                    onClick={handleClearRegions}
                    className="inline-flex items-center gap-1 text-xs text-[#555555] hover:text-[#6B4FBB] transition-colors"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                    Clear selection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom date range inputs */}
      {datePreset === 'custom' && (
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-neutral-200">
          <label className="text-sm text-[#555555]">
            Start
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
              className={cn(
                'ml-2 px-3 py-1.5 text-sm border rounded',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB]',
                dateError ? 'border-[#B91C1C]' : 'border-neutral-200'
              )}
            />
          </label>
          <label className="text-sm text-[#555555]">
            End
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
              className={cn(
                'ml-2 px-3 py-1.5 text-sm border rounded',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4FBB]',
                dateError ? 'border-[#B91C1C]' : 'border-neutral-200'
              )}
            />
          </label>
          {dateError && (
            <p className="text-sm text-[#B91C1C]" role="alert">
              {dateError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalFilterBar;
